import { useModalV2 } from 'common/modal/hooks/useModalV2';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useStartInternalSourcingWorkflowRunsMutation } from 'generated-graphql/hooks/apollo-hooks';
import { workflowBuilderLink } from 'modules/layout/constants/navbar';
import { buildWorkflowBuilderUrl } from 'modules/layout/utils/buildWorkflowBuilderUrl';
import { describeWorkflowPushResult } from 'modules/candidate-profile/utils/describeWorkflowPushResult';
import { useToast } from 'services/toast/hooks';
import { ToastType } from 'services/toast';

type OpenAddToWorkflowModalProps = {
  workflowId: string;
  /** Absent when the workflow could not be resolved; the copy falls back to a generic phrase. */
  workflowName: string | null;
  candidateProfileIds: string[];
  onAdded: () => void;
};

/**
 * The confirm shown when a recruiter arrived from the workflow builder to populate one
 * workflow. No picker: the builder named the workflow in the hand-off URL, so all that is left
 * is confirming who goes into it.
 *
 * Two ways forward rather than one, because the recruiter may have more people to find: adding
 * can either finish the round trip back to the builder, or stay here to keep searching. Both
 * enrol identically — they differ only in where the recruiter ends up.
 */
export const useAddToWorkflowModal = () => {
  const { openModal } = useModalV2();
  const { showToast } = useToast();
  const organization = useOrganization();
  const [startInternalSourcingWorkflowRuns] =
    useStartInternalSourcingWorkflowRunsMutation();

  const openAddToWorkflowModal = ({
    workflowId,
    workflowName,
    candidateProfileIds,
    onAdded
  }: OpenAddToWorkflowModalProps) => {
    const workflowUrl = buildWorkflowBuilderUrl(
      workflowBuilderLink,
      organization.id,
      workflowId
    );
    const count = candidateProfileIds.length;

    const push = async (): Promise<boolean> => {
      try {
        const { data } = await startInternalSourcingWorkflowRuns({
          variables: { input: { workflowId, candidateProfileIds } }
        });

        const { didEnrol, toast } = describeWorkflowPushResult({
          result: data?.startInternalSourcingWorkflowRuns,
          sent: count,
          workflowLabel: workflowName ?? 'workflow',
          workflowUrl
        });

        showToast(toast);
        return didEnrol;
      } catch {
        showToast({
          title: 'Could not add these candidates to the workflow',
          message: 'Nobody was added. Try again in a moment.',
          type: ToastType.Error
        });
        return false;
      }
    };

    openModal({
      width: '380px',
      stackActions: true,
      title: count === 1 ? 'Add 1 person' : `Add ${count} people`,
      subtitle: workflowName
        ? `They will be added to the ${workflowName} workflow immediately.`
        : 'They will be added to the workflow immediately.',
      primaryButtonText: 'Add and return to workflow',
      tertiaryButtonText: 'Add and continue searching',
      secondaryButtonText: 'Cancel',
      onPrimaryAction: async () => {
        // Reporting failure keeps the dialog open so the selection is not lost.
        if (!(await push())) {
          return false;
        }
        // Same tab: this finishes the errand the builder sent the recruiter on.
        window.location.href = workflowUrl;
        return true;
      },
      onTertiaryAction: async () => {
        if (!(await push())) {
          return false;
        }
        onAdded();
        return true;
      }
    });
  };

  return { openAddToWorkflowModal };
};
